<form [formGroup]="form" class="p-fluid">
    <!-- Details Card -->
    <div class="card surface-card border-1 surface-border border-round shadow-2 mb-3">
        <div class="card-header p-3 border-bottom-1 surface-border surface-100">
            <h6 class="m-0">Details</h6>
        </div>
        <div class="card-body p-3">
            <!-- Row 1: Identifier, Form ID, Version -->
            <div class="flex flex-wrap gap-3 mb-3">
                <div class="field mb-0" style="min-width: 250px; flex: 1;">
                    <label for="identifier" class="block text-900 font-medium mb-1 text-sm">
                        Identifier
                        <i class="pi pi-info-circle text-500 ml-1 cursor-pointer"
                            pTooltip="Optional - will be auto-generated from name if not provided" tooltipPosition="top"
                            *ngIf="currentCrudMode === CrudFormMode.CREATE">
                        </i>
                        <i class="pi pi-info-circle text-500 ml-1 cursor-pointer"
                            pTooltip="Cannot be changed after creation" tooltipPosition="top"
                            *ngIf="currentCrudMode === CrudFormMode.UPDATE">
                        </i>
                    </label>
                    <input pInputText id="identifier" formControlName="identifier" class="w-full"
                        placeholder="my-form-identifier" />
                    <div *ngIf="form.get('identifier')?.invalid && form.get('identifier')?.touched && currentCrudMode === CrudFormMode.CREATE"
                        class="text-red-500 mt-1">
                        <small *ngIf="form.get('identifier')?.errors?.['pattern']">
                            Identifier must be URL-safe (lowercase letters, numbers, and hyphens only, cannot start
                            or end with
                            hyphen).
                        </small>
                    </div>
                </div>

                <div class="field mb-0" style="min-width: 200px; max-width: 300px; flex: 1;">
                    <label for="id" class="block text-900 font-medium mb-1 text-sm">Form ID</label>
                    <input pInputText id="id" formControlName="id" class="w-full" [readOnly]="true" />
                </div>

                <div class="field mb-0" style="min-width: 100px; max-width: 100px;">
                    <label for="version" class="block text-900 font-medium mb-1 text-sm">
                        Version
                        <i class="pi pi-info-circle text-500 ml-1 cursor-pointer" pTooltip="Managed by system"
                            tooltipPosition="top">
                        </i>
                    </label>
                    <input pInputText id="version" formControlName="version" class="w-full" [readOnly]="true" />
                </div>
            </div>

            <!-- Row 2: Name, Description -->
            <div class="flex flex-wrap gap-3 mb-3">
                <div class="field mb-0" style="min-width: 250px; flex: 1;">
                    <label for="name" class="block text-900 font-medium mb-1 text-sm">
                        Name (This is permanent so check for spelling)
                        <span class="p-error">*</span>
                    </label>
                    <input pInputText id="name" formControlName="name" class="w-full" placeholder="My Form Name" />
                    <div *ngIf="form.get('name')?.invalid && form.get('name')?.touched" class="text-red-500 mt-1">
                        <small>Name is required.</small>
                    </div>
                </div>

                <div class="field mb-0" style="min-width: 300px; flex: 2;">
                    <label for="description" class="block text-900 font-medium mb-1 text-sm">
                        Description
                    </label>
                    <textarea pInputTextarea id="description" formControlName="description" class="w-full" rows="3"
                        placeholder="Form description..."></textarea>
                </div>
            </div>

            <!-- Row 3: Status, Active Status -->
            <div class="flex flex-wrap gap-3">
                <div class="field mb-0" style="min-width: 250px; flex: 1;">
                    <label for="status" class="block text-900 font-medium mb-1 text-sm">
                        Status
                        <span class="p-error">*</span>
                    </label>
                    <p-dropdown formControlName="status" [options]="statusOptions" optionLabel="label"
                        optionValue="value" placeholder="Select status" class="w-full">
                    </p-dropdown>
                    <div *ngIf="form.get('status')?.invalid && form.get('status')?.touched" class="text-red-500 mt-1">
                        <small>Status is required.</small>
                    </div>
                </div>

                <div class="field mb-0" style="min-width: 200px; flex: 1;">
                    <label class="block text-900 font-medium mb-1 text-sm">
                        Active Status
                    </label>
                    <div class="flex align-items-center h-3rem">
                        <span class="inline-flex align-items-center gap-2">
                            <i class="pi"
                                [class]="form.get('isActive')?.value ? 'pi-check-circle text-green-500' : 'pi-times-circle text-red-500'">
                            </i>
                            <span
                                [class]="form.get('isActive')?.value ? 'text-green-600 font-medium' : 'text-red-600 font-medium'">
                                {{ form.get('isActive')?.value ? 'Active' : 'Inactive' }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Form Configuration Card -->
    <div class="card surface-card border-1 surface-border border-round shadow-2 mb-3">
        <div class="card-header p-3 border-bottom-1 surface-border surface-100">
            <h6 class="m-0">Form Configuration</h6>
        </div>
        <div class="card-body p-3">
            <div class="formgrid grid">
                <div class="field col-12 mb-2">
                    <div>
                        <label for="formData" class="block text-900 font-medium mb-1 text-sm">
                            Form Builder
                        </label>
                        <div class="flex flex-wrap gap-2 mt-1">
                            <button pButton type="button"
                                [class]="isFormDataEmpty ? 'p-button-outlined p-button-danger flex-1' : 'p-button-outlined flex-1'"
                                style="min-width: 180px;" [label]="isFormDataEmpty ? 'No Data' : 'Raw JSON'"
                                icon="pi pi-code" (click)="openFormDataDialog()">
                            </button>
                            <button pButton type="button" class="p-button-outlined flex-1" style="min-width: 180px;"
                                label="Preview" icon="pi pi-eye" (click)="openSurveyPreviewDialog()">
                            </button>
                            <button pButton type="button" class="p-button flex-1" style="min-width: 180px;"
                                label="Editor" icon="pi pi-pencil" (click)="openSurveyCreatorDialog()">
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</form>

<!-- Form Data Dialog -->
<p-dialog [(visible)]="showFormDataDialog" [modal]="true" [closable]="true" [resizable]="true" [draggable]="false"
    header="Form Data (JSON)" [style]="{
        'width': '800px',
        'min-width': '320px',
        'height': '600px'
    }" (onHide)="closeFormDataDialog()">

    <div class="flex flex-column h-full" style="max-height: 520px;">
        <!-- Fixed Header Section -->
        <div
            class="flex flex-column sm:flex-row sm:justify-content-between sm:align-items-center gap-3 p-3 bg-blue-50 border-round mb-3">
            <div class="flex align-items-center gap-2">
                <i class="pi pi-info-circle text-blue-500"></i>
                <small class="text-700">
                    Make your changes and click "Update Form Data" to apply them to the form. JSON must be valid.
                </small>
            </div>
            <button pButton type="button" class="p-button-outlined p-button-sm w-full sm:w-auto"
                label="Copy to Clipboard" icon="pi pi-copy" (click)="copyFormData()">
            </button>
        </div>

        <!-- JSON Validation Status -->
        <div class="mb-3" *ngIf="dialogFormData.trim()">
            <div class="flex align-items-center gap-2 p-2 border-round"
                [class]="isValidJson ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
                <i class="pi" [class]="isValidJson ? 'pi-check-circle text-green-600' : 'pi-times-circle text-red-600'">
                </i>
                <small [class]="isValidJson ? 'text-green-700' : 'text-red-700'" class="flex-1 text-sm">
                    {{ isValidJson ? 'Valid JSON' : 'Invalid JSON: ' + jsonValidationError }}
                </small>
            </div>
        </div>

        <!-- Scrollable Content Area -->
        <div class="flex-1 p-3 bg-gray-50 border-round overflow-hidden" style="min-height: 0;">
            <textarea pInputTextarea [(ngModel)]="dialogFormData" (ngModelChange)="onJsonDataChange()"
                class="w-full h-full border-none overflow-auto"
                [class]="!isValidJson && dialogFormData.trim() ? 'border-red-300' : ''"
                [style]="{'font-family': 'JetBrains Mono, Consolas, Monaco, monospace', 'font-size': '14px', 'line-height': '1.5', 'background': 'transparent', 'resize': 'none'}"
                placeholder='{"fields": [], "config": {}}'>
            </textarea>
        </div>

        <!-- Fixed Footer Section -->
        <div class="flex flex-column sm:flex-row sm:justify-content-end gap-2 pt-3 border-top-1 surface-border mt-3">
            <button pButton type="button" class="p-button-outlined w-full sm:w-auto" label="Cancel" icon="pi pi-times"
                (click)="closeFormDataDialog()">
            </button>
            <button pButton type="button" class="p-button-primary w-full sm:w-auto" label="Update Form Data"
                icon="pi pi-check" [disabled]="!isValidJson && dialogFormData.trim()" (click)="updateFormData()">
            </button>
        </div>
    </div>
</p-dialog>

<!-- SurveyJS Creator Dialog -->
<p-dialog #dialog (onShow)="dialog.maximize()" [(visible)]="showSurveyCreatorDialog" [modal]="true" [closable]="false"
    [resizable]="false" [draggable]="false" [maximizable]="false" header="Form Editor" styleClass="fullscreen-dialog"
    [appendTo]="'body'" [style]="{width: '100vw', height: '100vh'}" (onHide)="closeSurveyCreatorDialog()">

    <ng-template pTemplate="header">
        <div class="flex justify-content-between align-items-center w-full">
            <span class="text-xl font-semibold">Form Editor</span>
            <div class="flex gap-2">
                <button pButton type="button" label="Cancel" icon="pi pi-times" class="p-button-text"
                    (click)="closeSurveyCreatorDialog()"></button>
                <button pButton type="button" label="Save" icon="pi pi-check" class="p-button-primary"
                    (click)="saveSurveyCreatorData()"></button>
            </div>
        </div>
    </ng-template>

    <!-- SurveyJS Creator Component -->
    <div class="survey-creator-container">
        <survey-creator *ngIf="surveyCreatorModel" [model]="surveyCreatorModel"></survey-creator>
    </div>
</p-dialog>

<!-- Survey Preview Dialog -->
<p-dialog #previewDialog (onShow)="previewDialog.maximize()" [(visible)]="showSurveyPreviewDialog" [modal]="true"
    [closable]="false" [resizable]="false" [draggable]="false" [maximizable]="false" header="Form Preview"
    styleClass="fullscreen-dialog" [appendTo]="'body'" [style]="{width: '100vw', height: '100vh'}"
    (onHide)="closeSurveyPreviewDialog()">

    <ng-template pTemplate="header">
        <div class="flex justify-content-between align-items-center w-full">
            <span class="text-xl font-semibold">Form Preview</span>
            <div class="flex gap-2">
                <button pButton type="button" [class]="isMobilePreview ? 'p-button-primary' : 'p-button-outlined'"
                    [label]="isMobilePreview ? 'Desktop View' : 'Mobile View'"
                    [icon]="isMobilePreview ? 'pi pi-desktop' : 'pi pi-mobile'" (click)="toggleMobilePreview()">
                </button>
                <button pButton type="button" label="Close" icon="pi pi-times" class="p-button-text"
                    (click)="closeSurveyPreviewDialog()"></button>
            </div>
        </div>
    </ng-template>

    <!-- Survey Preview Container -->
    <div class="survey-preview-container flex flex-column h-full">
        <!-- Info Header -->
        <div class="flex align-items-center gap-2 p-3 bg-blue-50 border-round mb-3">
            <i class="pi pi-info-circle text-blue-500"></i>
            <small class="text-700">
                This is a preview of your form. You can interact with it to see how it will behave for users.
            </small>
        </div>

        <!-- Scrollable Survey Container -->
        <div class="flex-1 overflow-auto flex justify-content-center">
            <div [class]="'border-1 surface-border border-round p-3 transition-all transition-duration-300 ' + 
                (isMobilePreview ? 'mobile-preview-container' : 'desktop-preview-container')"
                [style]="{'min-height': 'calc(100vh - 200px)'}">
                <survey *ngIf="surveyPreviewModel" [model]="surveyPreviewModel"></survey>
            </div>
        </div>
    </div>
</p-dialog>

<!-- Submit and Cancel Buttons -->
<div class="flex justify-content-end mt-3 gap-2" *ngIf="crudMode !== CrudFormMode.READ">
    <button pButton class="p-button-primary"
        [label]="(crudMode === CrudFormMode.UPDATE ? 'Update' : 'Add') + ' ' + 'Form'"
        [icon]="crudMode === CrudFormMode.UPDATE ? 'pi pi-save' : 'pi pi-plus'" (click)="onFormSubmit()">
    </button>
</div>

<!-- Confirm Dialog -->
<p-confirmDialog></p-confirmDialog>